import { Alert } from '../../alerts/models/Alert';
import { DeviceId } from '../models/Device';
import { DeviceInfo, DeviceProperty, PropertyId } from '../models/DeviceDetail';
import { DeviceSettingsPropertyVm } from '../models/DeviceSettingsPropertyVm';
import { DeviceStateUpdateExecutionInfo, ExecutionStatus } from '../models/Execution';
import { SectionItemEditionData } from '../models/SectionItemEditionData';
import { UiProfileProperty } from '../models/UiProfile';

import { filterAlertsByPropertyId } from './device-alert';
import buildViewer from './viewer-builder/buildViewer';

export function computeEditionStatus(
    gatewayAvailable: boolean,
    device: DeviceInfo,
    uiProperty: UiProfileProperty,
    property: DeviceProperty | undefined,
    isInIntervention: boolean,
    isPropertyUpdating: boolean,
): SectionItemEditionData {
    if (!uiProperty?.editable) {
        return { status: 'none' };
    }
    if (!isInIntervention) {
        return { status: 'disabled', tooltip: 'EQUIPMENT_EDIT_NOT_EDITABLE_HELP_TEXT' };
    }
    if (!gatewayAvailable || !device?.available) {
        return { status: 'disabled', tooltip: 'EQUIPMENT_EDIT_NOT_AVAILABLE_HELP_TEXT' };
    }
    if (!property) {
        return { status: 'disabled', tooltip: 'EQUIPMENT_STATE_VALUE_NOT_AVAILABLE_TOAST_ERROR' };
    }
    if (isPropertyUpdating) {
        return { status: 'inprogress' };
    }

    return { status: 'enabled' };
}

export function isPropertyUpdating(
    executionInfos: { deviceId: string; state: string; status: string }[] | undefined,
    deviceId: DeviceId,
    propertyId: PropertyId,
): boolean {
    const updateJob = executionInfos?.find((info) => info.deviceId === deviceId && info.state === propertyId);
    return (
        !!updateJob &&
        ![ExecutionStatus.COMPLETED, ExecutionStatus.FAILED].includes(updateJob.status as ExecutionStatus)
    );
}

export function buildSettingSection(
    propertyId: string,
    uiProperty: UiProfileProperty,
    deviceProperty: DeviceProperty | undefined,
    executionInfos: DeviceStateUpdateExecutionInfo[] | undefined,
    deviceId: string,
    device: DeviceInfo,
    gatewayAvailable: boolean,
    isInIntervention: boolean,
    deviceAlerts: Alert[],
): DeviceSettingsPropertyVm {
    const updating = isPropertyUpdating(executionInfos, deviceId, propertyId);
    const viewer = buildViewer(uiProperty, deviceProperty, { timestamp: device.lastUpdateTime });
    const edition = computeEditionStatus(
        gatewayAvailable,
        device,
        uiProperty,
        deviceProperty,
        isInIntervention,
        updating,
    );
    const alert = filterAlertsByPropertyId(deviceAlerts, propertyId);

    return {
        id: propertyId,
        label: uiProperty.label,
        value: viewer.type === 'text' ? viewer.value : '—',
        propertyId,
        editionStatus: edition.status === 'none' ? 'disabled' : edition.status,
        editionTooltip: edition.tooltip,
        alert,
    };
}
